import { apiUrl, authHeaders } from './auth';

export interface SaveReportPayload {
    name: string;
    notes?: string;
    visible_to_all_users: boolean;
    json_fields: string;
}

export interface SavedReport {
    id: number;
    name: string;
    notes: string;
    additional_info: string;
    visible_to_all_users: boolean;
    created_by_user: number | null;
    created_by_user_name: string | null;
    created_date: string;
    formatted_created_date: string;
    report_type: string;
    json_fields: string;
}

export interface PaginatedSavedReports {
    count: number;
    next: string | null;
    previous: string | null;
    results: SavedReport[];
}

const endpoint = (path = '') => `${apiUrl}/v2/reports/saved-report-types/${path}`;

export const saveReport = async (payload: SaveReportPayload) => {
    const headers = authHeaders();
    const resp = await fetch(endpoint(), {
        method: 'POST',
        headers: {
            ...headers,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
    });

    if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        const error: Error & { status?: number; data?: any } = new Error(
            errorData?.detail || `Failed to save report (${resp.status})`
        );
        error.status = resp.status;
        error.data = errorData;
        throw error;
    }

    return await resp.json();
};

export interface ListSavedReportsParams {
    page?: number;
    search?: string;
    created_by?: number | string;
    type?: string;
    created_from?: string;
    created_to?: string;
}

export const listSavedReports = async (
    params: ListSavedReportsParams = {}
): Promise<PaginatedSavedReports> => {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.search) query.set('search', params.search);
    if (params.created_by) query.set('created_by', String(params.created_by));
    if (params.type) query.set('type', params.type);
    if (params.created_from) query.set('created_from', params.created_from);
    if (params.created_to) query.set('created_to', params.created_to);
    const suffix = query.toString() ? `?${query.toString()}` : '';

    const resp = await fetch(endpoint() + suffix, {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!resp.ok) {
        throw new Error(`Failed to load saved reports (${resp.status})`);
    }

    return await resp.json();
};

export interface ReportCreator {
    id: number;
    name: string;
}

export const listSavedReportCreators = async (): Promise<ReportCreator[]> => {
    const resp = await fetch(endpoint('creators/'), {
        method: 'GET',
        headers: authHeaders(),
    });

    if (!resp.ok) {
        throw new Error(`Failed to load creators (${resp.status})`);
    }

    return await resp.json();
};

export const deleteSavedReport = async (id: number) => {
    const resp = await fetch(endpoint(`${id}/`), {
        method: 'DELETE',
        headers: authHeaders(),
    });

    if (!resp.ok) {
        throw new Error(`Failed to delete saved report (${resp.status})`);
    }
};
